import { $, $a, Aedu, Components } from 'global';
import React, { Component, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { showBulkDownloadModal } from 'js/bulk_download/show_bulk_download_modal';
import ReaderMessageForm from 'js/core_webpack/sitewide_business_elements/download/reader_message_form';
import { ReaderMessageContext } from 'js/liswp/work_actions/ReaderMessageContext';
import { NoThanksText } from 'js/bulk_download/all_readers_products_helper';
import { fsEvent } from 'js/fullstory';

/* A wrapper Component containing all the functionality of a work download button.
 * Renders its children and passes each child component an onDownloadClick and styleAsDownloaded (optional) props.
 * Therefore, the child component must be able to consume onDownloadClick and styleAsDownloaded props.
 *
 * - On a download click:
 *   - Downloads the primary attachment of the work (downloadableAttachments[0])
 *   - Handles data tracking
 *   - Considers auto-bookmarking the work
 *   - Considers showing upsells
 *   - Renders the post download reader message modal
 *   - Considers changing text from "Download" to "Downloaded"
 *
 * TODO(jacob): remove the last few dependencies ($a, Aedu.User)
 */
export default class DownloadButtonWrapper extends Component {
  static propTypes = {
    premium: PropTypes.bool.isRequired,
    work: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      isBookmarked: PropTypes.bool.isRequired,
      bulkDownloadPurchased: PropTypes.bool,
      // if not given downloadableAttachments[0], nothing gets rendered
      downloadableAttachments: PropTypes.arrayOf(
        PropTypes.shape({
          // url which begins the download
          // Note: not used directly, used to create this.downloadUrlWithTrackingToken
          downloadUrl: PropTypes.string.isRequired,
          id: PropTypes.number.isRequired,
        }),
      ).isRequired,
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
          // all author information required if showing reader message modal
          displayName: PropTypes.string,
          photo: PropTypes.string,
        }),
      ).isRequired,
    }).isRequired,

    // the location of this button, for tracking
    source: PropTypes.oneOf([
      'news-feed',
      'search-results',
      'swp-toolbar',
      'swp-bulk-download-modal',
      'swp_related_works_sidebar',
      'swp-related-works-grid',
      'sutd',
      'profile',
      'library',
      'email',
      'library',
      'swp-sticky-header',
      'coronavirus_page',
      'rankings',
      'regpath',
      'social-news',
    ]).isRequired,

    // when true will not start the download on button click, default false
    // will still open the bulk download modal if enabled
    gateDownload: PropTypes.bool,

    // when true will auto bookmark the work on download if the user has the public bookmarks setting enabled
    autoBookmark: PropTypes.bool.isRequired,
    // functions for bookmarking should be connected to redux store if given
    // if no redux store, default implementations are provided
    addToLibrary: PropTypes.func, // expects workId, contentType, source, callback
    removeFromLibrary: PropTypes.func, // expects workId, callback
    // when true will render the bookmarked toast on download click
    showAutoBookmarkToast: PropTypes.bool,

    // when true will fetch the post download data and render the reader message modal
    showReaderMessageModal: PropTypes.bool,

    // when true will render the BulkDownloadModal upsell
    showBulkDownloadUpsell: PropTypes.bool,

    // when true will allow a work to be bulk downloaded even when the user is not premium
    bulkDownladPurchased: PropTypes.bool,

    // when true will restyle the button from "Download" to "Downloaded" on click
    styleAsDownloaded: PropTypes.bool,

    // when true will show "View ↗" instead of "Download"
    isExternalSource: PropTypes.bool,

    // indicates this is going to be used to automatically download on page load.
    isAutoDownload: PropTypes.bool,

    // when given, this function is called with the startDownload function as a param
    // allows parent components to initiate the download
    setStartDownload: PropTypes.func,

    addTrackingTokenToUrl: PropTypes.func, // default is provided in this file

    bulkDownloadFilterCounts: PropTypes.shape({
      // Optional pre-loaded filter counts to save a request to backend
      topRelated: PropTypes.number,
      sameAuthor: PropTypes.number,
      citingThis: PropTypes.number,
      citedByThis: PropTypes.number,
    }),

    useNewTab: PropTypes.bool,
    // Our download endpoint can either redirect the user (in case of error or required captcha),
    // or it can download the file. So we can't always require the download attribute, but we
    // need it to fix bugs for auto=download.
    useDownloadAttribute: PropTypes.bool,
  };

  static defaultProps = {
    premium: false,
    gateDownload: false,
    showAutoBookmarkToast: true,
    showBulkDownloadUpsell: false,
    showReaderMessageModal: true,
    bulkDownloadPurchased: false,
    styleAsDownloaded: true,
    isExternalSource: false,
    isAutoDownload: false,
    useDownloadAttribute: false,
    addToLibrary: defaultAddToLibrary,
    removeFromLibrary: defaultRemoveFromLibrary,
    addTrackingTokenToUrl: addTrackingTokenToUrl,
  };

  static contextType = ReaderMessageContext;

  constructor(props) {
    super(props);
    this.loadedTimestamp = new Date();

    const attachment = props.work.downloadableAttachments[0];
    if (attachment != null) {
      this.downloadUrlWithTrackingToken = props.addTrackingTokenToUrl(
        attachment.downloadUrl,
        this.loadedTimestamp,
        props.source,
      );
    }

    this.state = {
      downloaded: false,
    };
  }

  componentDidMount() {
    // give the parent the ability to start downloads
    if (this.props.setStartDownload != null) {
      this.props.setStartDownload(this.startDownload.bind(this));
    }
  }

  render() {
    const {
      work,
      children,
      styleAsDownloaded,
      isExternalSource,
      isAutoDownload,
      useNewTab,
      useDownloadAttribute,
    } = this.props;
    const { downloaded } = this.state;

    if (work.downloadableAttachments[0] == null) return null;

    const newChildren = Children.map(children, (child) => {
      return cloneElement(child, {
        onDownloadClick: (e) => this.onDownloadClick(e),
        styleAsDownloaded: styleAsDownloaded && downloaded,
        isExternalSource: isExternalSource,
      });
    });

    let target = '';
    const isFirefox = navigator.userAgent.indexOf('Firefox') !== -1;
    if (isFirefox && (!isAutoDownload || useNewTab)) {
      target = '_blank';
    }

    const downloadAttribute = {};
    if (useDownloadAttribute) {
      downloadAttribute['download'] = '';
    }

    return (
      <div className="download-button--wrapper">
        <a
          style={{ visibility: 'hidden', display: 'none' }}
          href={this.downloadUrlWithTrackingToken}
          ref={(ref) => (this.downloadAnchor = ref)}
          target={target}
          {...downloadAttribute}
        />
        {newChildren}
      </div>
    );
  }

  downloadWork() {
    this.downloadAnchor.click();

    this.setState({ downloaded: true });

    if (this.shouldAutoBookmark) {
      this.autoBookmark();
    }

    if (
      this.showReaderMessagePrompt &&
      !this.context.readerMessageLimitReached
    ) {
      this.renderReaderMessageModal();
    }

    // Track post download page coverage when on swp
    const { source, work } = this.props;
    if (
      source === 'swp-bulk-download-modal' ||
      source === 'swp-sticky-header' ||
      source === 'swp-toolbar'
    ) {
      fetch(
        routes.api_v0_post_download_coverage_path({
          subdomain_param: 'api',
          work_id: work.id,
        }),
        {},
      );
    }
  }

  startDownload() {
    const {
      showBulkDownloadUpsell,
      premium,
      bulkDownloadPurchased,
      gateDownload,
    } = this.props;

    // The User Tagging Ping Survey contains an invisible div with this ID
    // when it wants to disable the download button
    if (document.getElementById('user-tagging-ping-prevent-download')) {
      const pingContainer = document.getElementById('ping-survey-container');
      pingContainer.classList.remove('wiggle');
      void pingContainer.offsetWidth;
      pingContainer.classList.add('wiggle');
      return;
    }

    if (premium || bulkDownloadPurchased) {
      this.downloadWork();
    } else {
      if (showBulkDownloadUpsell) {
        if (!gateDownload) {
          this.downloadWork();
          fsEvent('ds_liswp.bulk_download_upsell', {
            dsBucket: window.cwvAbTestBucket,
          });
        } else {
          fsEvent('ds_liswp.bulk_download_upsell.gated_download', {
            dsBucket: window.cwvAbTestBucket,
          });
        }
        this.renderBulkDownloadModal();
      } else {
        this.downloadWork();
      }
    }
  }

  onDownloadClick = (e) => {
    e.preventDefault();
    this.startDownload();
  };

  get showReaderMessagePrompt() {
    return this.props.showReaderMessageModal && !this.authoredByCurrentUser;
  }

  get shouldAutoBookmark() {
    return (
      this.props.autoBookmark &&
      !this.props.work.isBookmarked &&
      !this.authoredByCurrentUser &&
      Aedu.User.current &&
      Aedu.User.current.preferences &&
      Aedu.User.current.preferences.public_bookmarks
    );
  }

  get authoredByCurrentUser() {
    return (
      Aedu.User.current &&
      this.props.work.authors.some(
        (author) => author.id === Aedu.User.current.id,
      )
    );
  }

  autoBookmark() {
    this.props.addToLibrary(
      this.props.work.id,
      // TODO replace this with a better type check
      this.props.work.id instanceof String ? 'OpenalexWork' : 'Work',
      'download',
      () => this.props.showAutoBookmarkToast && this.showAutoBookmarkToast(),
    );
  }

  showAutoBookmarkToast() {
    Components.Utils.showToast(
      Components.Toasts.AutoBookmarkToast,
      {
        undoBookmark: () =>
          this.props.removeFromLibrary(this.props.work.id, () => {}),
        linkSettingsOnUndoBookmark: undefined,
      },
      {
        timeMs: 4000,
        clickToClose: false,
        verticalAlign: 'bottom',
      },
    );
  }

  renderReaderMessageModal() {
    const { work, showAutoBookmarkToast } = this.props;
    const author = work.authors[0];

    // Skip reader message modal if no authors (system-owned works)
    if (!author) return;

    let { source } = this.props;
    // TODO(jacob): standardize news feed sources
    if (source === 'news-feed') {
      source = 'news';
    }

    Components.Utils.showModal(
      ReaderMessageForm,
      {
        primaryAuthor: { displayName: author.displayName, photo: author.photo },
        workId: work.id,
        attachmentId: work.downloadableAttachments[0].id,
        readerMessageSource: source,
        showMessageToast: showAutoBookmarkToast,
        setReaderMessageLimitReached: this.context.setReaderMessageLimitReached,
      },
      { modalClassName: 'wide-modal' },
    );
  }

  renderBulkDownloadModal() {
    const { work, source, gateDownload, bulkDownloadPurchased } = this.props;
    const attachment = work.downloadableAttachments[0];
    showBulkDownloadModal({
      trigger: source + '-work-card-download-button',
      gatedDownload: gateDownload,
      work: {
        id: work.id,
        slug: work.slug,
        title: work.title,
        attachment: { url: attachment.downloadUrl, id: attachment.id },
        authors: work.authors,
      },
      noThanksText: NoThanksText.ThisPaper,
      purchased: bulkDownloadPurchased,
      readerMessageLimitReached: this.context.readerMessageLimitReached,
      setReaderMessageLimitReached: this.context.setReaderMessageLimitReached,
    });
  }
}

function addTrackingTokenToUrl(downloadUrl, loadedTimestamp, source) {
  const [url, queryParams, hashParams] = $a.decompose_url(downloadUrl);

  // TODO(jacob): standardize news feed sources
  let actualSource = source;
  if (source === 'news-feed') {
    actualSource = 'news';
  }

  if (!queryParams.some((qp) => qp[0] === 'ct')) {
    queryParams.push(['ct', generateTrackingToken(loadedTimestamp)]);
  }
  if (!queryParams.some((qp) => qp[0] === 's')) {
    queryParams.push(['s', actualSource]);
  }

  return $a.build_url(url, queryParams, hashParams);
}

function generateTrackingToken(loadedTimestamp) {
  const tokenParams = [
    Math.round(loadedTimestamp.getTime() / 1000),
    Math.round(Date.now() / 1000),
  ];
  if (Aedu.User.current != null) {
    tokenParams.push(Aedu.User.current.id);
  }
  return window.btoa(tokenParams.join(','));
}

function defaultAddToLibrary(workId, contentType, source, callback) {
  let fromDownloadAt = null;
  if (source === 'download') {
    fromDownloadAt = new Date();
  }

  $.ajax({
    type: 'POST',
    url: routes.api_v0_bookmarks_path({
      'bookmark[content_id]': workId,
      'bookmark[source]': source,
      'bookmark[from_download_at]': fromDownloadAt,
      'bookmark[content_type]': contentType,
      subdomain_param: 'api',
    }),
  }).then(() => callback());
}

function defaultRemoveFromLibrary(workId, callback) {
  $.ajax({
    type: 'DELETE',
    url: routes.destroy_for_work_api_v0_bookmarks_path({
      content_id: workId,
      subdomain_param: 'api',
    }),
  }).then(() => callback());
}
